import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/App';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Mail, Phone, Globe, MapPin, Building2, Heart, Users } from 'lucide-react';
import { toast } from 'sonner';

const Organizations = ({ user, onLogout }) => {
  const [organizations, setOrganizations] = useState([]);
  const [filteredOrgs, setFilteredOrgs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    filterOrganizations();
  }, [searchQuery, organizations, selectedType]);

  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/organizations');
      setOrganizations(response.data);
    } catch (error) {
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const filterOrganizations = () => {
    let filtered = organizations;
    
    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(org => org.org_type === selectedType);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (org) =>
          org.org_name.toLowerCase().includes(query) ||
          org.description.toLowerCase().includes(query) ||
          org.focus_areas.some((area) => area.toLowerCase().includes(query))
      );
    }
    
    setFilteredOrgs(filtered);
  };

  const handleSendMessage = (org) => {
    navigate('/messages', { state: { toUserId: org.user_id, toOrgName: org.org_name } });
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="organizations-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Organization Directory</h1>
          <p className="text-gray-600">Connect with nonprofits and service organizations</p>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search organizations by name, description, or focus area..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="organization-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Category Tabs */}
        <Tabs value={selectedType} onValueChange={setSelectedType} className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="all" className="flex items-center gap-2" data-testid="filter-all">
              <Building2 className="h-4 w-4" />
              All Organizations
            </TabsTrigger>
            <TabsTrigger value="nonprofit" className="flex items-center gap-2" data-testid="filter-nonprofit">
              <Heart className="h-4 w-4" />
              Nonprofits
            </TabsTrigger>
            <TabsTrigger value="service" className="flex items-center gap-2" data-testid="filter-service">
              <Users className="h-4 w-4" />
              Service Orgs
            </TabsTrigger>
          </TabsList>

          <TabsContent value={selectedType} className="mt-6">
            {/* Results */}
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading organizations...</p>
              </div>
            ) : filteredOrgs.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
            {filteredOrgs.map((org) => (
              <Card key={org.id} className="hover:shadow-lg transition-all duration-300" data-testid={`org-card-${org.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between mb-3">
                    <Badge className={org.org_type === 'nonprofit' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}>
                      {org.org_type === 'nonprofit' ? (
                        <><Heart className="h-3 w-3 mr-1 inline" /> Nonprofit</>
                      ) : (
                        <><Users className="h-3 w-3 mr-1 inline" /> Service Organization</>
                      )}
                    </Badge>
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">{org.org_name}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {org.description || 'No description available'}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Location (always shown) */}
                  {org.address && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{org.address}</span>
                    </div>
                  )}

                  {/* Contact Info (only for subscribers or hidden for free) */}
                  {org.email ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="h-4 w-4" />
                        <a href={`mailto:${org.email}`} className="hover:text-green-600">
                          {org.email}
                        </a>
                      </div>
                      {org.phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Phone className="h-4 w-4" />
                          <span>{org.phone}</span>
                        </div>
                      )}
                      {org.website && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Globe className="h-4 w-4" />
                          <a href={org.website} target="_blank" rel="noopener noreferrer" className="hover:text-green-600">
                            Visit Website
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm text-amber-800 mb-2">
                        🔒 Contact details hidden on free plan
                      </p>
                      <Link to="/pricing">
                        <Button size="sm" variant="outline" className="w-full border-amber-400 text-amber-700 hover:bg-amber-100">
                          Upgrade to View Contacts
                        </Button>
                      </Link>
                    </div>
                  )}

                  {/* Focus Areas */}
                  {org.focus_areas && org.focus_areas.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Focus Areas:</p>
                      <div className="flex flex-wrap gap-2">
                        {org.focus_areas.map((area, index) => (
                          <Badge key={index} variant="secondary" className="bg-green-100 text-green-700">
                            {area}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {org.user_id !== user.id && org.email && (
                    <Button
                      onClick={() => handleSendMessage(org)}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`send-message-button-${org.id}`}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Send Message
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No organizations found matching your criteria.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Organizations;